import React, { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import Header from '../comp_reusable/report_page_universal/ReportHeader'
import useStandardToaster from '../../ui_reusable/StandardToaster'
import ReportFooter from './scenario_report/ScenarioReportFooter'
import ReportDocument from '../comp_reusable/report_page_universal/ReportDocument'
import UniversalLoader from '../../ui_reusable/UniversalLoader'
import { useAuth } from '../../../context/AuthContext'
// Import scenario-specific injection configuration for custom components
import injectionConfig from './injectionConfig.json'
// Import scenario-specific custom component registry for component resolution
import { CUSTOM_COMPONENT_REGISTRY } from './CustomComponentRegistry'
import { useScenarioReportStore } from './stores/scenarioReportStore'
import type { RouteTowardsReportPackage } from '../types/ReportTypes'

// Interface for route parameters
interface RouteParams extends Record<string, string | undefined> {
  reportId?: string
}

const ScenarioReport: React.FC = () => {
  // Access route information and navigation
  const location = useLocation()
  const params = useParams<RouteParams>()

  // Get store functions and values using selectors to avoid unnecessary re-renders
  const resetAllStoreData = useScenarioReportStore(
    (state) => state.resetAllStoreData,
  )
  const initializeFromNavigation = useScenarioReportStore(
    (state) => state.initializeFromNavigation,
  )
  const setUserName = useScenarioReportStore((state) => state.setUserName)

  // Get initial report data for the Header component
  const initialReportData = useScenarioReportStore((state) => state.anyReport)

  // Get report document for the ReportDocument component
  const reportDocument = useScenarioReportStore((state) =>
    state.getReportDocument(),
  )

  // Get loading state from store
  const loading = useScenarioReportStore(
    (state) => state.fetchingReportIsLoading,
  )

  // Utility hooks for notifications and auth
  const { pushToast } = useStandardToaster()
  const { user } = useAuth()

  /**
   * Extract navigation parameters and destructure location state
   */
  const routedReportId = params.reportId
  const locationState = location.state as RouteTowardsReportPackage | null
  const { data: preLoadedReport } = locationState || {}

  /**
   * Reset store state when component mounts to ensure clean state
   */
  useEffect(() => {
    resetAllStoreData()
    /* eslint-disable-next-line */
  }, []) // Empty dependency array means this runs only on mount

  /**
   * Initialize user name in store when user is available
   */
  useEffect(() => {
    if (user?.name) {
      setUserName(user.name)
    }
  }, [user?.name, setUserName])

  /**
   * Initialize the store with navigation data
   * This handles data loading and state setup for scenarios
   */
  useEffect(() => {
    initializeFromNavigation({
      data: preLoadedReport,
      routedReportId,
      pushToast,
    })
    /* eslint-disable-next-line */
  }, [preLoadedReport, routedReportId])

  // Determine if we're still in loading state
  const isLoading = loading || !initialReportData

  // Render the full report with header, content and footer
  return (
    <div style={{ position: 'relative' }}>
      {/* Show loading indicator while data is being fetched */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '50vh',
            zIndex: 1000,
          }}
        >
          <UniversalLoader style={{ borderRadius: '1rem' }} show={true} />
        </div>
      )}

      <Header data={initialReportData} store={useScenarioReportStore} />

      {reportDocument && (
        <ReportDocument
          isNewlyGeneratedReport={!routedReportId}
          injectionConfig={injectionConfig} // Pass scenario-specific injection config for custom components
          componentRegistry={CUSTOM_COMPONENT_REGISTRY} // Pass custom component registry
          reportTitle="Scenario report" // Pass report-specific title
          store={useScenarioReportStore}
        />
      )}

      <ReportFooter />
    </div>
  )
}

export default ScenarioReport
